package one.healthium.med.presentation.navigation.home

import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.bringToFront
import com.arkivanov.decompose.router.stack.childStack
import com.arkivanov.decompose.router.stack.childStackWebNavigation
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.router.webhistory.WebNavigation
import com.arkivanov.decompose.value.Value
import kotlinx.serialization.Serializable
import one.healthium.content.navigation.content.DefaultContentComponent
import one.healthium.content.navigation.insights.DefaultInsightsComponent
import one.healthium.content.navigation.main.DefaultMainContentComponent
import one.healthium.content.navigation.shopping_list.DefaultShoppingListComponent
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.ShoppingListModel
import one.healthium.intercom.Intercom
import one.healthium.med.presentation.navigation.today.DefaultTodayComponent
import one.healthium.profile.navigation.DefaultProfileMainComponent
import one.healthium.profile.ui.food_history.models.FoodItemUiModel
import one.healthium.tasks.navigation.DefaultDailyTasksComponent
import one.primehealth.app.DeepLink
import kotlin.time.Instant

class DefaultHomeComponent(
    componentContext: ComponentContext,
    private val onInsightClicked: (insightId: String) -> Unit,
    private val onLogFoodClicked: (taskId: String?) -> Unit,
    private val onLogWaterClicked: (taskId: String?) -> Unit,
    private val onLogWeightClicked: (taskId: String?) -> Unit,
    private val onLogSymptomsClicked: (taskId: String?) -> Unit,
    private val onMealPlanClicked: (String) -> Unit,
    private val onWeightHistoryClicked: (Float, Instant) -> Unit,
    private val onWorkoutClicked: (String) -> Unit,
    private val onFoodHistoryItemClicked: (FoodItemUiModel) -> Unit,
    private val onActionClicked: (ActionModel) -> Unit,
    private val onUpdateNameClicked: () -> Unit,
    private val onUpdateAgeClicked: () -> Unit,
    private val onUpdateHeightClicked: () -> Unit,
    private val onUpdateInitialWeightClicked: () -> Unit,
    private val onUpdateTargetWeightClicked: () -> Unit,
) : HomeComponent, ComponentContext by componentContext {

    private val nav = StackNavigation<Config>()

    private val _stack = childStack(
        source = nav,
        serializer = Config.serializer(),
        initialStack = { listOf(Config.Today) },
        childFactory = ::child,
    )

    private fun child(config: Config, componentContext: ComponentContext): HomeComponent.Child {
        return when (config) {
            Config.Today -> HomeComponent.Child.TodayChild(
                DefaultTodayComponent(
                    componentContext = componentContext,
                    onMealPlanClicked = onMealPlanClicked,
                    onLogFoodClicked = { onLogFoodClicked(null) },
                    onLogWaterClicked = { onLogWaterClicked(null) },
                    onLogWeightClicked = { onLogWeightClicked(null) },
                    onLogSymptomsClicked = { onLogSymptomsClicked(null) },
                )
            )

            Config.DailyTasks -> HomeComponent.Child.DailyTasksChild(
                DefaultDailyTasksComponent(
                    componentContext = componentContext,
                    onLogWeightTaskClicked = onLogWeightClicked,
                    onLogWaterTaskClicked = onLogWaterClicked,
                    onLogFoodTaskClicked = onLogFoodClicked,
                    onReadInsightTaskClicked = {
                        if (isCompactMode) {
                            onInsightClicked.invoke(it)
                        } else {
                            onActionClicked(ActionModel.Insight(it))
                        }
                    },
                    onDoWorkoutTaskClicked = {
                        if (isCompactMode) {
                            onWorkoutClicked.invoke(it)
                        } else {
                            onActionClicked(ActionModel.Workout(it))
                        }
                    },
                    onLogSymptomsTaskClicked = onLogSymptomsClicked,
                )
            )

            Config.Insights -> HomeComponent.Child.InsightsChild(
                DefaultInsightsComponent(
                    componentContext = componentContext,
                    onActionClicked = ::onActionClicked,
                )
            )

            Config.MainContent -> HomeComponent.Child.MainContentChild(
                DefaultMainContentComponent(
                    componentContext = componentContext,
                    onActionClicked = ::onActionClicked,
                )
            )

            Config.Chat -> HomeComponent.Child.ChatChild
            Config.Profile -> HomeComponent.Child.ProfileChild(
                DefaultProfileMainComponent(
                    componentContext = componentContext,
                    onContactUsClicked = ::onChatTabClicked,
                    onWeightHistoryClicked = onWeightHistoryClicked,
                    onFoodHistoryItemClicked = onFoodHistoryItemClicked,
                    onUpdateNameClicked = onUpdateNameClicked,
                    onUpdateAgeClicked = onUpdateAgeClicked,
                    onUpdateHeightClicked = onUpdateHeightClicked,
                    onUpdateInitialWeightClicked = onUpdateInitialWeightClicked,
                    onUpdateTargetWeightClicked = onUpdateTargetWeightClicked,
                )
            )

            is Config.Content -> HomeComponent.Child.Content(
                DefaultContentComponent(
                    componentContext = componentContext,
                    options = config.options,
                    onBackClicked = ::onBackClicked,
                    onActionClicked = ::onActionClicked,
                )
            )

            is Config.ShoppingList -> HomeComponent.Child.ShoppingList(
                DefaultShoppingListComponent(
                    componentContext = componentContext,
                    shoppingList = config.shoppingList,
                    onBackClicked = ::onBackClicked,
                )
            )
        }
    }

    override val stack: Value<ChildStack<*, HomeComponent.Child>> = _stack

    override var isCompactMode: Boolean = true
        set(value) {
            field = value
            if (_stack.value.items.size == 1) {
                if (value && _stack.value.active.instance is HomeComponent.Child.DailyTasksChild) {
                    nav.bringToFront(Config.Today)
                } else if (value.not() && _stack.value.active.instance is HomeComponent.Child.TodayChild) {
                    nav.bringToFront(Config.DailyTasks)
                }
            }
        }

    override val webNavigation: WebNavigation<*> =
        childStackWebNavigation(
            navigator = nav,
            stack = _stack,
            enableHistory = false,
            serializer = Config.serializer(),
            childSelector = {
                when (val child = it.instance) {
                    is HomeComponent.Child.ProfileChild -> child.component
                    else -> null
                }
            }
        )

    override fun onTodayTabClicked() {
        nav.bringToFront(Config.Today)
    }

    override fun onDailyTasksTabClicked() {
        nav.bringToFront(Config.DailyTasks)
    }

    override fun onInsightsTabClicked() {
        nav.bringToFront(Config.Insights)
    }

    override fun onMainContentTabClicked() {
        nav.bringToFront(Config.MainContent)
    }

    override suspend fun onChatTabClicked() {
        Intercom.present()
    }

    override fun onProfileTabClicked() {
        nav.bringToFront(Config.Profile)
    }

    override fun onDeepLink(deepLink: DeepLink) {
        when (deepLink) {
            DeepLink.Today -> onTodayTabClicked()
            DeepLink.DailyTasks -> onDailyTasksTabClicked()
            DeepLink.Insights -> onInsightsTabClicked()
            else -> Unit
        }
    }

    override fun onLogFoodClicked() {
        onLogFoodClicked(null)
    }

    override fun onLogWaterClicked() {
        onLogWaterClicked(null)
    }

    override fun onLogWeightClicked() {
        onLogWeightClicked(null)
    }

    override fun onLogSymptomsClicked() {
        onLogSymptomsClicked(null)
    }

    override fun onMealPlanClicked(id: String) {
        onActionClicked(ActionModel.MealPlan(id))
    }

    private fun onActionClicked(action: ActionModel) {
        if (isCompactMode.not()) {
            when (action) {
                is ActionModel.Insight -> nav.pushNew(Config.Content(ContentOptions.Insight(action.id)))
                is ActionModel.Recipe -> nav.pushNew(Config.Content(ContentOptions.Recipe(action.id)))
                is ActionModel.Category -> nav.pushNew(Config.Content(ContentOptions.Category(action.id)))
                is ActionModel.Video -> onActionClicked.invoke(action)
                is ActionModel.Workout -> nav.pushNew(Config.Content(ContentOptions.Workout(action.id)))
                is ActionModel.ShoppingList -> nav.pushNew(Config.ShoppingList(action.shoppingList))
                is ActionModel.MealPlan -> nav.pushNew(Config.Content(ContentOptions.MealPlan(action.id)))
                is ActionModel.LogMeal -> onActionClicked.invoke(action)
                is ActionModel.Anchor -> Unit
                is ActionModel.UpdateFoodPreferences -> onActionClicked.invoke(action)
                is ActionModel.Favorite -> Unit
                is ActionModel.Share -> Unit
            }
        } else {
            onActionClicked.invoke(action)
        }
    }

    private fun onBackClicked() {
        if (_stack.value.items.size > 1) {
            nav.pop()
        }
    }

    @Serializable
    private sealed interface Config {
        @Serializable
        data object Today : Config

        @Serializable
        data object DailyTasks : Config

        @Serializable
        data object Insights : Config

        @Serializable
        data object MainContent : Config

        @Serializable
        data object Chat : Config

        @Serializable
        data object Profile : Config

        @Serializable
        data class Content(
            val options: ContentOptions,
        ) : Config

        @Serializable
        data class ShoppingList(
            val shoppingList: ShoppingListModel
        ) : Config
    }
}